import { commandDefinition, optionsDefinition } from "../../Commons/Options/CommandBasedOptionsParser";
import { CommonOptions } from "../CommonOptions/CommonOptions";
import { devModeOption, logVerbosityOption } from "../CommonOptions/CommonOptionDefintions";

import { BuildCacheMode, parseBuildCacheMode } from "./BuildCacheMode";

export interface BuildFormsOptions extends CommonOptions {
    watch: boolean;
    clean: boolean;
    formGlobs: string[];
    upgradeFormJson: boolean;
    useLastStableVersion: boolean;
    usePrereleaseVersion?: string;
    useVersionFromCache: boolean;
    mode: "development" | "production";
    buildCacheMode: BuildCacheMode;
    buildCandyWidget: boolean;
}

export const buildFormsOptionsDefinition = commandDefinition<"build", BuildFormsOptions>(
    "build",
    "build",
    optionsDefinition(
        {
            alias: "w",
            name: "watch",
            defaultValue: { watch: false },
            parse: input => ({ watch: input }),
            doesNotRequireValue: true,
            description:
                "Watch-режим. Выполняет сборку форм, а затем пересобирает при изменениях. В том числе движка, генератора и статических ресурсов.",
        },
        {
            name: "clean",
            defaultValue: { clean: false },
            parse: input => ({ clean: input }),
            doesNotRequireValue: true,
            description:
                "Полностью очищает целевой каталог ресурсов. Используется при полной сборке форм для продакшена",
        },
        {
            name: "mode",
            defaultValue: { mode: "development" },
            parse: (input: string) => ({ mode: input }),
            description:
                "Разделение сборок на продовую (mode=production) и не продовую (mode=development). По умолчанию development",
        },
        {
            name: "upgrade-form-json",
            defaultValue: { upgradeFormJson: false },
            parse: input => ({ upgradeFormJson: input }),
            doesNotRequireValue: true,
            description: "Форсирует изменение формата файла form.json на новый",
        },
        {
            name: "build-cache",
            defaultValue: { buildCacheMode: BuildCacheMode.ReadOnly },
            parse: (input: string) => ({ buildCacheMode: parseBuildCacheMode(input) }),
            description:
                "Задаёт режим использования кэша сборки. Возможные значения:\n" +
                "  disabled - не использовать кэш\n" +
                "  readonly - использовать кэш для сборки, но не обновляет его (значение по умолчанию)\n" +
                "  update - использовать кэш для сборки и презаписывать, если не совпадает\n" +
                "  force-update - всегда выполнять сборку и обновить значения в кэше\n" +
                "Отключается при использовании watch-режима",
        },
        {
            name: "use-last-stable-version",
            defaultValue: { useLastStableVersion: false },
            parse: input => ({ useLastStableVersion: input }),
            doesNotRequireValue: true,
            description:
                "Устанавливает версию движка в файла `form.json` в последнюю стабильную версию. Аналогичен запуску candy-build с параметром -ge master",
        },
        {
            name: "use-prerelease-version",
            defaultValue: { usePrereleaseVersion: undefined as undefined | string },
            parse: (input: string) => ({ usePrereleaseVersion: input }),
            description:
                "Устанавливает версию движка в файла form.json в последнюю пререлизную версию в заданным preid. В файле версии генератора и движка будут установлены в pre:some-preid.",
        },
        {
            alias: "g",
            name: "gfvs",
            defaultValue: { formGlobs: [] },
            parse: (input: string[]) => ({ formGlobs: input }),
            defaultOption: true,
            multiple: true,
            description: "Одна или несколько GFV или GFV-масок для пересборки. Может содержать значки `?` и `*`.",
        },
        {
            name: "use-version-from-cache",
            defaultValue: { useVersionFromCache: false },
            parse: (input: boolean) => ({ useVersionFromCache: input }),
            description: "Использовать версию движка и генератора из файловой системы",
            doesNotRequireValue: true,
        },
        {
            name: "build-candy-widget",
            doesNotRequireValue: true,
            defaultValue: { buildCandyWidget: false },
            parse: (input: boolean) => ({ buildCandyWidget: input }),
            description: "Собирать candy-виджет",
        },
        devModeOption,
        logVerbosityOption
    ),
    {
        description:
            "Выполняет сборку одной или нескольких форм.\n" +
            "Подробнее: https://git.skbkontur.ru/forms/candy/-/blob/master/Tools/README.md#candy-build",

        detailedDescription: `
Основной сценарий: выполняет сборку форм в каталог с ресурсами. Действия, которые выполняет команда:

* Получение движка форм версии указанной в \`form.json\`,
* получение генератора форм версии указанной в \`form.json\`,
* генерация и компиляция формы в каталог ресурсов,
* создание файлов \`{gfv}.metas.json\` в каталоге ресурсов
* копирование конвертеров в каталог ресурсов,
* копирование схемы в каталог ресурсов.
`.trim(),

        examples: `
\`\`\`
# Сборка формы 104816
candy build 104816
\`\`\`

\`\`\`
# Сборка все формы группы 1
candy build 1?????
\`\`\`

\`\`\`
# Собрать форму 104816 и следить за изменениями
candy build 104816 -w
\`\`\`

\`\`\`
# Использовать последнюю пререлизную версию движка с preid=bso
candy build 104816 --use-prerelease-version bso
\`\`\`
`.trim(),

        extendedDetails: `
### Версионирование

При сборке форм, учитывается версия движка и генератора указанные файле form.json. Версии могут указывать не на конкретную версию, а адресовать диапазон в соответствии с [semver](https://semver.org/lang/ru/).
В этом случае будет выбрана последняя подходящая версия.

Файл form.json должен выглядеть следующим образом:
\`\`\`
{
    // Опционально
    "themes": ["имя-темы", ...],
    "dependencies": {
        "engine": "[версия-движка]",
        "generator": "[версия-генератора]"
    }
}
\`\`\`

\`версия-движка\`, \`версия-генератора\` - Соответственно, диапазоны версий. На данный момент всегда совпадают.

Допустимые варианты версий:
* Версия в формате [semver](https://semver.org/lang/ru/).
* Версия с префиксом pre. Например, \`pre:my-stange-branch-name\`, где \`my-stange-branch-name\` - название предрелизной версии, выпускаемой разработчиками Candy, для предварительного выпуска форм.
Версия с префиксом pre используются для движков, которые не являются стабильными и используются для предварительных сборок форм.
`.trim(),
    }
);
